import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router';
import styles from './CategoryPageRedesign.module.scss';
import Header from '../components/Header/Header';
import ScrollToTop from '../components/ScrollToTop/ScrollToTop';
import Footer from '../components/Footer/Footer';
import StoresAPI from '@api/stores-api';
import Product from '@models/product';
import { generateProductDetailsUrl } from '@utils/urlUtils';
import { formatDiscountPercentage } from '@utils/formatDiscountPercentage';

const CategoryPageRedesign: React.FC = () => {
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('category');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'popular' | 'price-low' | 'price-high' | 'newest'>('popular');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const storesAPI = useMemo(() => new StoresAPI(), []);

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    // Scroll to top when category changes
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [categoryId]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        // Fetch products - in real implementation, filter by category
        const feeds = await storesAPI.getHomeFeed();
        const allProducts = feeds.flatMap(feed => feed.items || []);
        // For demo, use all products. In production, filter by category
        setProducts(allProducts.slice(0, 24));
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryId, storesAPI]);

  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => a.buyPrice - b.buyPrice);
      case 'price-high':
        return sorted.sort((a, b) => b.buyPrice - a.buyPrice);
      case 'newest':
        // Sort by discount percentage (higher discount = newer/promoted) or keep original order
        return sorted.sort((a, b) => {
          const discountA = a.discountPercentage || 0;
          const discountB = b.discountPercentage || 0;
          if (discountB !== discountA) {
            return discountB - discountA;
          }
          // If discounts are equal, prioritize hot offers
          if (a.hotOffer && !b.hotOffer) return -1;
          if (!a.hotOffer && b.hotOffer) return 1;
          return 0;
        });
      default:
        return sorted;
    }
  }, [products, sortBy]);

  const convertProductToCardFormat = (product: Product, index: number) => {
    const productUrl = product.id ? generateProductDetailsUrl({
      id: product.id,
      title: product.title || product.extendedTitle || 'Product'
    }) : '#';

    return {
      id: product.id,
      name: product.title || product.extendedTitle || 'Product',
      price: product.buyPriceString || `₹${product.buyPrice}`,
      originalPrice: product.displayPriceString && product.displayPrice !== product.buyPrice 
        ? product.displayPriceString 
        : undefined,
      discount: product.discountPercentage || undefined,
      gradient: product.background || 'linear-gradient(135deg, #42A5F5, #1E88E5)',
      imageUrl: product.poster || undefined,
      icon: 'fas fa-gamepad',
      tag: product.hotOffer ? 'Hot' : product.buttonDiscountText || undefined,
      link: productUrl
    };
  };

  const handleImageError = (productId: string) => {
    setImageErrors(prev => new Set(prev).add(productId));
  };

  const productPlaceholderUrl = `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`;

  const getIconColor = (gradient: string) => {
    // All vibrant gradients work well with white icons for contrast
    return 'white';
  };

  return (
    <div className={styles.categoryPage}>
      <Header />

      <div className={styles.contentWrapper}>
        <div className={styles.pageHeader}>
          <div className={styles.pageTitleSection}>
            <h1 className={styles.pageTitle}>
              {categoryId ? categoryId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'All Categories'}
            </h1>
            <p className={styles.pageSubtitle}>
              {loading ? 'Loading products...' : `Discover ${sortedProducts.length} amazing products`}
            </p>
          </div>
        </div>

        <div className={styles.filtersBar}>
          <div className={styles.resultsInfo}>
            <div className={styles.resultsCount}>
              <i className="fas fa-box"></i>
              <span>{loading ? 'Loading...' : `${sortedProducts.length} Products`}</span>
            </div>
          </div>
          <div className={styles.filterControls}>
            <div className={styles.sortGroup}>
              <i className="fas fa-sort-amount-down"></i>
              <div className={styles.selectWrapper}>
                <select 
                  className={styles.sortSelect}
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                >
                  <option value="popular">Most Popular</option>
                  <option value="newest">Newest First</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
            </div>
            <div className={styles.viewToggle}>
              <button
                className={`${styles.viewButton} ${viewMode === 'grid' ? styles.active : ''}`}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
                title="Grid view"
              >
                <i className="fas fa-th"></i>
              </button>
              <button
                className={`${styles.viewButton} ${viewMode === 'list' ? styles.active : ''}`}
                onClick={() => setViewMode('list')}
                aria-label="List view"
                title="List view"
              >
                <i className="fas fa-list"></i>
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading products...</p>
          </div>
        ) : sortedProducts.length === 0 ? (
          <div className={styles.emptyState}>
            <i className="fas fa-inbox"></i>
            <h3>No products found</h3>
            <p>Try adjusting your filters or check back later.</p>
          </div>
        ) : (
          <div className={`${styles.productsGrid} ${styles[viewMode]}`}>
            {sortedProducts.map((product, index) => {
              const cardProduct = convertProductToCardFormat(product, index);
              return (
                <Link
                  key={product.id || index}
                  to={cardProduct.link || '#'}
                  className={styles.productCard}
                >
                  <div
                    className={`${styles.productImage} ${cardProduct.imageUrl ? styles.hasImage : ''}`}
                    style={{ 
                      background: cardProduct.imageUrl && !imageErrors.has(product.id || `product-${index}`) ? 'transparent' : cardProduct.gradient 
                    }}
                  >
                    {cardProduct.discount != null && cardProduct.discount > 0 && (
                      <div className={styles.discountBadge}>
                        {formatDiscountPercentage(cardProduct.discount)}% OFF
                      </div>
                    )}
                    {cardProduct.tag && (
                      <div className={styles.productTag} data-tag={cardProduct.tag}>
                        {cardProduct.tag}
                      </div>
                    )}
                    <button
                      className={styles.wishlistBtn}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      aria-label="Add to wishlist"
                    >
                      <i className="far fa-heart"></i>
                    </button>
                    {cardProduct.imageUrl && !imageErrors.has(product.id || `product-${index}`) ? (
                      <img 
                        src={cardProduct.imageUrl} 
                        alt={cardProduct.name}
                        className={styles.productImageContent}
                        onError={() => handleImageError(product.id || `product-${index}`)}
                        onLoad={(e) => {
                          // Check if image actually loaded (some servers return 200 with error image)
                          const img = e.target as HTMLImageElement;
                          if (img.naturalWidth === 0 || img.naturalHeight === 0) {
                            handleImageError(product.id || `product-${index}`);
                          }
                        }}
                      />
                    ) : (
                      <img
                        src={productPlaceholderUrl}
                        alt={cardProduct.name}
                        className={styles.productImageContent}
                      />
                    )}
                  </div>
                  <div className={styles.productInfo}>
                    <div className={styles.productName} title={cardProduct.name}>
                      {cardProduct.name}
                    </div>
                    <div className={styles.productPriceContainer}>
                      {cardProduct.originalPrice && (
                        <span className={styles.originalPrice}>{cardProduct.originalPrice}</span>
                      )}
                      <span className={styles.productPrice}>{cardProduct.price}</span>
                      <button
                        className={styles.addToCartIcon}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        aria-label="Add to Cart"
                      >
                        <img
                          src={process.env.PUBLIC_URL + "/images/icons/add-to-cart.svg"}
                          alt="Add to Cart"
                          width={24}
                          height={24}
                        />
                      </button>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default CategoryPageRedesign;

